import { useEffect, useState, type ReactNode } from 'react';
import { playComercio } from '../audio/mixer';
import { api } from '../api';
import {
  ApiError, type Pack, type VendaCategoria, type VendaDestino, type VendaItem, type VendaJanela
} from '../api/types';
import { numero, t } from '../i18n/idioma';
import { nomeDoItem } from '../i18n/nomes';
import { iconeDaPrata, prefetchIcons } from '../assets/items';
import { ItemIcon } from './ItemIcon';
import { stones } from './itemUi';

/** Prata pesa 0,02 st (GDD 11.1). */
const PRATA_PESO = 0.02;

type Chip = 'tudo' | 'loot' | VendaCategoria;
const CHIPS: readonly Chip[] = ['tudo', 'loot', 'equipamento', 'material', 'consumivel'];

interface Props {
  active: boolean;
  tick: number;
  cidade: string;
  /** Em ordem de caçada: a janela mostra o cartão de volta, como as lojas. */
  cacando: boolean;
  lugarCacada: string;
  natal: string;
  dead: boolean;
  onPack(pack: Pack): void;
  onDiario(texto: string, tom: 'system' | 'warn'): void;
  onVoltar(): void;
}

/**
 * Menu de vendas (techspec-vendas §05). A cidade compra o que está na mochila pelo preço que o
 * admin definiu; o servidor confere tudo de novo, então o que se vê aqui é só a vitrine.
 */
export function VenderGump({ active, tick, cidade, cacando, lugarCacada, natal, dead, onPack, onDiario, onVoltar }: Props) {
  const [janela, setJanela] = useState<VendaJanela | null>(null);
  const [pack, setPack] = useState<Pack | null>(null);
  const [semMundo, setSemMundo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [aba, setAba] = useState<'vender' | 'recomprar'>('vender');
  const [chip, setChip] = useState<Chip>('tudo');
  const [sel, setSel] = useState<Record<string, number>>({});
  const [destino, setDestino] = useState<VendaDestino | null>(null);
  const [conf, setConf] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    if (!active) return;
    let live = true;
    void api.vendas()
      .then(j => {
        if (!live) return;
        setJanela(j);
        setAgora(Date.now());
        setSemMundo(false);
        setErro(null);
        prefetchIcons(j.itens.map(i => i.defId));
        setSel(cur => limparSelecao(cur, j.itens));
      })
      .catch(err => {
        if (!live) return;
        if (err instanceof ApiError && err.code === 'npc.needWorld') setSemMundo(true);
        else setErro(err instanceof ApiError ? err.message : t('venda.erro'));
      });
    void api.getPack().then(p => { if (live) setPack(p); }).catch(() => {});
    return () => { live = false; };
  }, [active, tick, recarga]);

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setAgora(Date.now()), 20_000);
    return () => window.clearInterval(id);
  }, [active]);

  const regras = janela?.regras;
  const destinoAtual: VendaDestino = destino ?? regras?.destinoPadrao ?? 'cofre';
  const itens = janela?.itens ?? [];
  const vendaveis = itens.filter(podeVender);
  const naoVende = itens.filter(i => !podeVender(i));
  // Cada linha é uma pilha do servidor (techspec-pilha-de-pecas): peça igual já vem junta, com a quantidade.
  const visiveis = vendaveis.filter(i => chip === 'tudo' || (chip === 'loot' ? i.loot : i.categoria === chip));
  const marcados = vendaveis
    .map(i => ({ item: i, qty: Math.min(sel[i.itemId] ?? 0, livre(i)) }))
    .filter(x => x.qty > 0 && !x.item.travado);
  const total = marcados.reduce((s, x) => s + x.qty * x.item.preco, 0);
  const pesoSai = marcados.reduce((s, x) => s + x.qty * x.item.peso, 0);
  const pesoDepois = pack ? pack.weight - pesoSai + (destinoAtual === 'mochila' ? total * PRATA_PESO : 0) : 0;
  const semPeso = destinoAtual === 'mochila' && pack !== null && marcados.length > 0 && pesoDepois > pack.weightMax + 1e-9;
  const vivas = (janela?.recompras ?? []).filter(r => Date.parse(r.ate) > agora);
  const caras = regras && regras.confirmarPeca > 0 ? marcados.filter(x => x.item.preco >= regras.confirmarPeca) : [];
  const precisaConfirmar = !!regras && marcados.length > 0
    && (total >= regras.confirmarTotal || caras.length > 0);

  const lootLivre = vendaveis.filter(i => i.loot && !i.travado);

  /** Marca `qty` unidades da pilha (até o que está livre dela). */
  function marcar(item: VendaItem, qty: number) {
    if (item.travado) return;
    const q = Math.min(livre(item), Math.max(0, Math.trunc(qty) || 0));
    setSel(cur => ({ ...cur, [item.itemId]: q }));
  }

  async function travar(item: VendaItem) {
    const novo = !item.travado;
    setJanela(j => j && ({ ...j, itens: j.itens.map(i => i.defId === item.defId ? { ...i, travado: novo } : i) }));
    if (novo) setSel(cur => {
      const prox = { ...cur };
      for (const i of itens) if (i.defId === item.defId) delete prox[i.itemId];
      return prox;
    });
    try {
      await api.travarVenda(item.defId, novo);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('venda.erro'));
      setRecarga(n => n + 1);
    }
  }

  async function vender() {
    if (!marcados.length || busy) return;
    setConf(false);
    setBusy(true);
    setErro(null);
    try {
      const r = await api.vender({
        itens: marcados.map(x => ({ itemId: x.item.itemId, qty: x.qty })),
        destino: destinoAtual,
        esperado: total
      });
      playComercio();
      setSel({});
      setPack(r.pack);
      onPack(r.pack);
      onDiario(t('venda.diario.vendeu', {
        n: r.linhas,
        total: numero(r.total),
        destino: t(r.destino === 'cofre' ? 'venda.diario.noCofre' : 'venda.diario.naMochila')
      }), 'system');
      setRecarga(n => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'venda.precoMudou') setRecarga(n => n + 1);
      setErro(err instanceof ApiError ? err.message : t('venda.erro'));
    } finally {
      setBusy(false);
    }
  }

  /** Recompra uma venda (a pilha vendida volta inteira). */
  async function recomprar(id: string, nome: string) {
    if (busy) return;
    setBusy(true);
    setErro(null);
    try {
      const r = await api.recomprar(id);
      setPack(r.pack);
      onPack(r.pack);
      onDiario(t('venda.diario.recomprou', { item: nome, total: numero(r.valor) }), 'system');
      setRecarga(n => n + 1);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('venda.erro'));
      setRecarga(n => n + 1);
    } finally {
      setBusy(false);
    }
  }

  if (dead) return <Fora svg="fantasma" titulo={t('venda.fora.morto.titulo')} texto={t('venda.fora.morto.texto')} />;
  if (cacando) {
    return (
      <Fora svg="cidade" titulo={t('venda.fora.longe.titulo')} texto={t('venda.fora.cacando.texto', { lugar: lugarCacada, cidade: natal })}>
        <button type="button" className="btn btn--primary btn--tiny" onClick={onVoltar}>{t('hud.trava.voltar', { cidade: natal })}</button>
      </Fora>
    );
  }
  if (semMundo) return <Fora svg="cidade" titulo={t('venda.fora.longe.titulo')} texto={t('venda.fora.mundo.texto')} />;
  if (!janela) {
    return erro
      ? <p className="notice" role="alert">{erro}</p>
      : <p className="vd-vazio">{t('comum.montando')}</p>;
  }
  if (janela.motivo === 'morto') return <Fora svg="fantasma" titulo={t('venda.fora.morto.titulo')} texto={t('venda.fora.morto.texto')} />;
  if (janela.motivo === 'cacando') {
    return (
      <Fora svg="cidade" titulo={t('venda.fora.longe.titulo')} texto={t('venda.fora.cacando.texto', { lugar: lugarCacada || cidade, cidade: natal })}>
        <button type="button" className="btn btn--primary btn--tiny" onClick={onVoltar}>{t('hud.trava.voltar', { cidade: natal })}</button>
      </Fora>
    );
  }
  if (janela.motivo === 'longe' || janela.motivo === 'mundo')
    return <Fora svg="cidade" titulo={t('venda.fora.longe.titulo')} texto={t('venda.fora.longe.texto')} />;

  const recompraMin = regras?.recompraMin ?? 0;

  return (
    <>
      <div className="vd__abas" role="tablist">
        <button type="button" role="tab" aria-selected={aba === 'vender'} onClick={() => setAba('vender')}>{t('venda.abaVender')}</button>
        <button type="button" role="tab" aria-selected={aba === 'recomprar'} onClick={() => setAba('recomprar')}>
          {t('venda.abaRecomprar')}{vivas.length > 0 && <b>{vivas.length}</b>}
        </button>
      </div>
      {erro && <p className="notice" role="alert">{erro}</p>}

      {aba === 'recomprar' ? (
        <>
          <p className="vd__nota">{recompraMin > 0 ? t('venda.recompra.nota', { n: recompraMin }) : t('venda.recompra.desligada')}</p>
          <div className="vd__lista">
            {vivas.length === 0 ? <div className="vd-vazio">{t('venda.recompra.vazio')}</div> : vivas.map(r => {
              const nome = nomeDoItem(r.defId, r.nome);
              const min = Math.max(1, Math.ceil((Date.parse(r.ate) - agora) / 60_000));
              const sub = `${r.qty > 1 ? t('venda.recompra.un', { n: r.qty }) : t('venda.recompra.peca')} · ${t('venda.recompra.vendidoPor', { n: numero(r.valor) })}`;
              return (
                <div className="vd-rc" key={r.id}>
                  <ItemIcon id={r.defId} size={36} quantity={r.qty > 1 ? r.qty : undefined} />
                  <span className="vd-l__n">
                    <b>{nome}</b>
                    <small>{sub}</small>
                  </span>
                  <time className={min <= 2 ? 'is-logo' : undefined}>{t('venda.recompra.min', { n: min })}</time>
                  <button type="button" className="btn btn--tiny" disabled={busy} onClick={() => void recomprar(r.id, nome)}>
                    {t('venda.recompra.botao')}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="vd__pe">
            <div className="vd__dest">{t('venda.recompra.pe')}</div>
            <div className="vd__total"><Moeda n={(janela.prataCofre ?? 0) + (pack?.silver ?? janela.prataMochila)} /></div>
          </div>
        </>
      ) : !janela.aberta ? (
        <Fora svg="cidade" titulo={t('venda.fora.desligada.titulo')} texto={t('venda.fora.desligada.texto')} />
      ) : (
        <>
          <div className="vd__barra">
            <div className="mkt-chips" role="group" aria-label={t('venda.filtro')}>
              {CHIPS.map(c => (
                <button key={c} type="button" className={`mkt-chip${chip === c ? ' is-on' : ''}`} aria-pressed={chip === c} onClick={() => setChip(c)}>
                  {t(`venda.chip.${c}`)}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="btn btn--tiny vd__marcar"
              disabled={lootLivre.length === 0}
              onClick={() => setSel(cur => {
                const prox = { ...cur };
                for (const i of lootLivre) prox[i.itemId] = livre(i);
                return prox;
              })}
            >
              {t('venda.marcarLoot')}
            </button>
          </div>

          <div className="vd__lista">
            <div className="vd-sec">{t('venda.secMochila')} <span>{visiveis.length > 0 ? t('venda.secMochilaDica') : ''}</span></div>
            {visiveis.length === 0
              ? <div className="vd-vazio">{t('venda.vazio')}</div>
              : visiveis.map(i => (
                <Linha key={i.itemId} item={i} qty={Math.min(sel[i.itemId] ?? 0, livre(i))}
                  busy={busy} onMarcar={marcar} onTravar={x => void travar(x)} />
              ))}
            {naoVende.length > 0 && (
              <>
                <div className="vd-sec">{t('venda.secNao')} <span>{naoVende.length}</span></div>
                {naoVende.map(i => (
                  <div className="vd-nao" key={i.itemId}>
                    <ItemIcon id={i.defId} size={28} quantity={i.qty > 1 ? i.qty : undefined} />
                    <b>{nomeDoItem(i.defId, i.nome)}</b>
                    <small>{t(i.equipado ? 'venda.nao.equipado' : !i.compra ? 'venda.nao.naoCompra' : 'venda.nao.troca')}</small>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="vd__pe">
            <div className="vd__dest">
              {t('venda.receberNo')}
              <span className="p-seg p-seg--mini" role="radiogroup" aria-label={t('venda.destino')}>
                {(['cofre', 'mochila'] as const).map(d => (
                  <button key={d} type="button" role="radio" aria-checked={destinoAtual === d} onClick={() => setDestino(d)}>
                    {t(`venda.${d}`)}
                  </button>
                ))}
              </span>
              {destinoAtual === 'cofre'
                ? <span className="vd-conta">{t('venda.contaCofre', { a: numero(janela.prataCofre), b: numero(janela.prataCofre + total) })}</span>
                : pack && <span className={`vd-conta${semPeso ? ' is-mau' : ''}`}>{t('venda.contaPeso', { a: stones(pack.weight), b: stones(pesoDepois), max: stones(pack.weightMax) })}</span>}
            </div>
            <div className="vd__total">
              <div>
                <Moeda n={total} />
                <small>{marcados.length === 0 ? t('venda.nadaMarcado') : t('venda.nItens', { n: marcados.length })}</small>
              </div>
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy || marcados.length === 0 || semPeso}
                onClick={() => { if (precisaConfirmar) setConf(true); else void vender(); }}
              >
                {t('venda.vender')}
              </button>
            </div>
            {semPeso
              ? <p className="vd__motivo" role="alert">{t('venda.semPesoMotivo', { a: stones(pesoDepois), b: stones(pack!.weightMax) })}</p>
              : <p className="vd__nota vd__nota--pe">{recompraMin > 0 ? t('venda.nota', { n: recompraMin }) : t('venda.notaFinal')}</p>}
          </div>

          {conf && (
            <div className="vd-conf" role="dialog" aria-modal="true" aria-labelledby="vdConfT">
              <div className="vd-conf__c">
                <h3 id="vdConfT">{t('venda.conf.titulo', { n: marcados.length })}</h3>
                <p>{t('venda.conf.texto', { total: numero(total), destino: t(destinoAtual === 'cofre' ? 'venda.diario.noCofre' : 'venda.diario.naMochila') })}</p>
                {caras.length > 0 && (
                  <>
                    <p>{t('venda.conf.caras')}</p>
                    <ul>{caras.map(x => <li key={x.item.itemId}>{nomeDoItem(x.item.defId, x.item.nome)}<span>{numero(x.qty * x.item.preco)}</span></li>)}</ul>
                  </>
                )}
                {recompraMin > 0 && <p className="vd__nota">{t('venda.conf.recompra', { n: recompraMin })}</p>}
                <div className="vd-conf__a">
                  <button type="button" className="btn" onClick={() => setConf(false)}>{t('comum.cancelar')}</button>
                  <button type="button" className="btn btn--primary" data-foco-inicial disabled={busy} onClick={() => void vender()}>{t('venda.vender')}</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

function Linha({ item, qty, busy, onMarcar, onTravar }: {
  item: VendaItem;
  qty: number;
  busy: boolean;
  onMarcar(item: VendaItem, qty: number): void;
  onTravar(item: VendaItem): void;
}) {
  const nome = nomeDoItem(item.defId, item.nome);
  const pilha = item.qty > 1;
  const max = livre(item);
  const sub = [
    item.loot ? t('venda.sub.loot') : t(`venda.cat.${item.categoria}`),
    pilha ? t('venda.sub.qtd', { n: item.qty }) : t('venda.sub.peca')
  ];
  return (
    <div className={`vd-l${qty > 0 && !item.travado ? ' is-sel' : ''}${item.travado ? ' is-travado' : ''}`}>
      <input
        type="checkbox"
        checked={qty > 0 && !item.travado}
        disabled={item.travado || busy}
        aria-label={t('venda.marcar', { item: nome })}
        onChange={e => onMarcar(item, e.target.checked ? max : 0)}
      />
      <ItemIcon id={item.defId} size={36} quantity={pilha ? item.qty : undefined} />
      <span className="vd-l__n"><b title={nome}>{nome}</b><small>{sub.join(' · ')}</small></span>
      <span className="vd-l__p"><Moeda n={item.preco} pequena /><small>{t(pilha ? 'venda.cada' : 'venda.aPeca')}</small></span>
      {pilha ? (
        <span className="vd-l__q">
          <input
            className="market-qty"
            type="text"
            inputMode="numeric"
            value={qty}
            disabled={item.travado || busy}
            aria-label={t('mercado.quantidadeDe', { item: nome })}
            onChange={e => onMarcar(item, Number(e.target.value.replace(/\D/g, '') || '0'))}
          />
          <button type="button" disabled={item.travado || busy} onClick={() => onMarcar(item, max)}>{t('venda.tudo')}</button>
        </span>
      ) : <span className="vd-l__q"><span className="so">{qty > 0 ? '1' : '—'}</span></span>}
      <span className="vd-l__t">{qty > 0 && !item.travado ? numero(qty * item.preco) : '—'}</span>
      <button
        type="button"
        className="vd-trava"
        aria-pressed={item.travado}
        disabled={busy}
        title={t(item.travado ? 'venda.destravar' : 'venda.travar')}
        aria-label={`${t(item.travado ? 'venda.destravarCurto' : 'venda.travarCurto')} ${nome}`}
        onClick={() => onTravar(item)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d={item.travado ? 'M8 11V7a4 4 0 0 1 8 0v4' : 'M8 11V7a4 4 0 0 1 7.5-2'} />
        </svg>
      </button>
    </div>
  );
}

function Moeda({ n, pequena }: { n: number; pequena?: boolean }) {
  return (
    <span className={pequena ? 'vd-moeda vd-moeda--p' : 'vd-moeda'}>
      <img src={iconeDaPrata()} alt="" width={pequena ? 14 : 18} height={pequena ? 14 : 18} draggable={false} />
      {numero(n)}
    </span>
  );
}

function Fora({ svg, titulo, texto, children }: { svg: 'cidade' | 'fantasma'; titulo: string; texto: string; children?: ReactNode }) {
  return (
    <div className="vd-fora">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        {svg === 'fantasma'
          ? <path d="M6 20V10a6 6 0 0 1 12 0v10l-2-1.5L14 20l-2-1.5L10 20l-2-1.5zM10 10h.01M14 10h.01" />
          : <path d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6" />}
      </svg>
      <b>{titulo}</b>
      <p>{texto}</p>
      {children}
    </div>
  );
}

function podeVender(i: VendaItem): boolean {
  return i.compra && !i.equipado && livre(i) > 0;
}

function livre(i: VendaItem): number {
  return Math.max(0, i.qty - i.preso);
}

/** Tira da seleção o que não está mais na mochila (vendeu, recomprou, foi para o banco). */
function limparSelecao(cur: Record<string, number>, itens: readonly VendaItem[]): Record<string, number> {
  const ids = new Set(itens.map(i => i.itemId));
  const prox: Record<string, number> = {};
  for (const [id, q] of Object.entries(cur)) if (ids.has(id) && q > 0) prox[id] = q;
  return prox;
}
